import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import { GridIcon, GroupsIcon } from '@treams/treams-icons';
import {
	FeatureName,
	type Group,
	GroupType,
	useTalentReviewTemplatesQuery,
} from '@treams/treams-network';
import {
	Container,
	Empty,
	type ITimeMachine,
	Jumbo,
	Skeleton,
	timeMachineLocalDates,
} from '@treams/treams-ui';

import { useGroupSelection } from '../../goals/group-selector/new/useGroupSelection';
import { AnalyticsFilter, canSelectDepartment } from '../components/AnalyticsFilter';
import { TalentReviewActivityCard } from './TalentReviewActivityCard';
import { TalentReviewDepartmentsTable } from './TalentReviewDepartmentsTable';
import { type TalentReviewCardView, TalentReviewGridCard } from './TalentReviewGridCard';
import { TalentReviewRetentionRiskCard } from './TalentReviewRetentionRiskCard';
import { TalentReviewSuccessorsCard } from './TalentReviewSuccessorsCard';
import { useTalentReviewAnalytics } from './useTalentReviewAnalytics';
import { useTalentReviewAnalyticsSearch } from './useTalentReviewAnalyticsSearch';

const CardsRow = styled(Flex)`
	flex-wrap: wrap;
	align-items: stretch;
	gap: 16px;
	margin-bottom: 16px;
`;

const DistributionColumn = styled(Box)`
	flex: 2 1 520px;
	min-width: 0;
`;

const ActivityColumn = styled(Box)`
	flex: 1 1 340px;
	min-width: 0;
`;

type TalentReviewAnalyticsProps = {
	headerFooter?: ReactNode;
	timeMachine: ITimeMachine;
	onChangeTimeMachine: (newTimeMachine: ITimeMachine) => void;
};

function TalentReviewAnalyticsSkeleton({ hasSuccession }: { hasSuccession?: boolean }) {
	return (
		<Container py={4} wide={true} minWidth={0}>
			<CardsRow>
				<DistributionColumn>
					<TalentReviewGridCard.Skeleton />
				</DistributionColumn>
				<ActivityColumn>
					<TalentReviewActivityCard.Skeleton />
				</ActivityColumn>
			</CardsRow>
			<TalentReviewDepartmentsTable.Skeleton />
			{hasSuccession && (
				<Box mt={3}>
					<TalentReviewSuccessorsCard.Skeleton />
				</Box>
			)}
			<Box mt={3}>
				<TalentReviewRetentionRiskCard.Skeleton />
			</Box>
		</Container>
	);
}

// Selected departments, plus teams that sit under one of them. With every
// department selected, teams that have no department are included too.
function teamIDsForTable(
	groups: Group[],
	departmentIDs: string[],
	includeUnassignedTeams: boolean,
) {
	const selected = new Set(departmentIDs);
	const byID = new Map(groups.map((group) => [group.id, group]));
	const teamIDs = groups
		.filter((group) => {
			if (group.type !== GroupType.Team) {
				return false;
			}

			if (hasSelectedDepartmentAncestor(group, byID, selected)) {
				return true;
			}

			return includeUnassignedTeams && !hasDepartmentAncestor(group, byID);
		})
		.map((group) => group.id);

	return [...departmentIDs, ...teamIDs];
}

function hasDepartmentAncestor(group: Group, byID: Map<string, Group>) {
	const seen = new Set<string>();
	let parentID = group.parent?.id;

	while (parentID && !seen.has(parentID)) {
		const parent = byID.get(parentID);

		if (parent?.type === GroupType.Department) {
			return true;
		}

		seen.add(parentID);
		parentID = parent?.parent?.id;
	}

	return false;
}

function hasSelectedDepartmentAncestor(
	group: Group,
	byID: Map<string, Group>,
	selected: Set<string>,
) {
	const seen = new Set<string>();
	let parentID = group.parent?.id;

	while (parentID && !seen.has(parentID)) {
		if (selected.has(parentID)) {
			return true;
		}

		seen.add(parentID);
		parentID = byID.get(parentID)?.parent?.id;
	}

	return false;
}

function TalentReviewEmptyPlaceholder({
	icon,
	title,
	description,
}: {
	icon: ReactNode;
	title: string;
	description: string;
}) {
	return (
		<Box style={{ position: 'relative', zIndex: 0, maxHeight: '500px', overflow: 'hidden' }}>
			<Skeleton.Empty contents={null}>
				<TalentReviewAnalyticsSkeleton />
			</Skeleton.Empty>
			<Box
				style={{
					position: 'absolute',
					bottom: 0,
					left: 0,
					width: '100%',
					height: '400px',
					background: 'linear-gradient(to bottom, transparent, white)',
					pointerEvents: 'none',
				}}
			/>
			<Box
				style={{
					position: 'absolute',
					left: '50%',
					top: '50%',
					transform: 'translate(-50%, -70%)',
				}}
			>
				<Empty icon={icon} title={title} description={description} />
			</Box>
		</Box>
	);
}

export function TalentReviewAnalytics(props: TalentReviewAnalyticsProps) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const hasSuccession = useFeature(FeatureName.Succession);

	const [showNorm, setShowNorm] = useState(false);
	const [view, setView] = useState<TalentReviewCardView>('grid');

	const groupSelection = useGroupSelection({
		setPermissionBasedDefault: true,
		canSelect: canSelectDepartment,
	});

	const groupKey = groupSelection.groupsInSelection
		.map((group) => group.id)
		.sort()
		.join(',');
	const groupIDs = useMemo(() => (groupKey ? groupKey.split(',') : []), [groupKey]);
	const tableGroupIDs = useMemo(
		() =>
			teamIDsForTable(
				groupSelection.allGroups,
				groupIDs,
				!groupSelection.hasExplicitGroupSelection,
			),
		[groupSelection.allGroups, groupIDs, groupSelection.hasExplicitGroupSelection],
	);

	const { data: templatesData, loading: templatesLoading } = useTalentReviewTemplatesQuery({
		variables: { isArchived: false, first: 1000 },
	});

	const templateEdges = templatesData?.talentReviewTemplates.edges;
	const hasNoTemplates = templateEdges !== undefined && templateEdges.length === 0;

	const templateOptions = useMemo(
		() =>
			templateEdges?.map(({ node }) => ({
				title: localize(node.title).text ?? '',
				value: node.id,
			})) ?? [],
		[templateEdges, localize],
	);

	const { templateID, roleID, setTemplateID, setRoleID, onDepartmentChange } =
		useTalentReviewAnalyticsSearch({
			templateOptions,
			groupSelection,
		});

	const { localDate, previousLocalDate } = timeMachineLocalDates(props.timeMachine);

	const { loading, data } = useTalentReviewAnalytics({
		templateID,
		groupIDs,
		tableGroupIDs,
		roleID,
		localDate,
		previousLocalDate,
	});

	const tableRows = useMemo(() => {
		if (!data) {
			return [];
		}

		const typeByID = new Map(groupSelection.allGroups.map((group) => [group.id, group.type]));

		return data.departments.map((row) => ({
			...row,
			type: typeByID.get(row.id) ?? GroupType.Department,
		}));
	}, [data, groupSelection.allGroups]);

	const showSkeleton =
		templatesLoading ||
		(!hasNoTemplates && groupIDs.length > 0 && (loading || !templateID) && !data);

	const handleToggleNorm = () => setShowNorm((current) => !current);

	return (
		<>
			<Jumbo title={t('analytics.overview.title')} footer={props.headerFooter} wide={true} />
			<>
				{hasNoTemplates ? (
					<Container wide={true}>
						<Flex py={5} alignItems="center" justifyContent="center">
							<Empty
								icon={<GridIcon />}
								title={t('analytics.talentReview.empty.title')}
								description={t('analytics.talentReview.empty.description')}
							/>
						</Flex>
					</Container>
				) : (
					<>
						<AnalyticsFilter
							talentReviewTemplateID={templateID}
							talentReviewTemplateOptions={templateOptions}
							onTalentReviewTemplateChange={setTemplateID}
							roleID={roleID}
							onRoleChange={setRoleID}
							departments={groupSelection}
							onDepartmentChange={onDepartmentChange}
							timeMachine={props.timeMachine}
							onTimeMachineChange={props.onChangeTimeMachine}
						/>

						{groupIDs.length === 0 && !templatesLoading ? (
							<TalentReviewEmptyPlaceholder
								icon={<GroupsIcon />}
								title={t('analytics.talentReview.empty.noDepartments.title')}
								description={t('analytics.talentReview.empty.noDepartments.description')}
							/>
						) : showSkeleton ? (
							<TalentReviewAnalyticsSkeleton hasSuccession={hasSuccession} />
						) : data ? (
							<Container py={4} wide={true} minWidth={0}>
								<CardsRow>
									<DistributionColumn>
										<TalentReviewGridCard
											rows={data.rows}
											xAxisLabel={data.xAxisLabel}
											yAxisLabel={data.yAxisLabel}
											placedCount={data.placedCount}
											allDepartments={!groupSelection.hasExplicitGroupSelection}
											hasNorm={data.hasNorm}
											showNorm={data.hasNorm && showNorm}
											onToggleNorm={handleToggleNorm}
											view={view}
											onViewChange={setView}
											gridName={
												templateOptions.find((option) => option.value === templateID)?.title ?? ''
											}
										/>
									</DistributionColumn>
									<ActivityColumn>
										<TalentReviewActivityCard
											placedCount={data.placedCount}
											eligibleCount={data.eligibleCount}
											previousPlacedCount={data.previousPlacedCount}
											previousEligibleCount={data.previousEligibleCount}
										/>
									</ActivityColumn>
								</CardsRow>
								<TalentReviewDepartmentsTable groups={tableRows} />
								{hasSuccession && (
									<Box mt={3}>
										<TalentReviewSuccessorsCard />
									</Box>
								)}
								<Box mt={3}>
									<TalentReviewRetentionRiskCard rows={data.retention} />
								</Box>
							</Container>
						) : (
							<TalentReviewEmptyPlaceholder
								icon={<GridIcon />}
								title={t('analytics.talentReview.empty.noPlacements.title')}
								description={t('analytics.talentReview.empty.noPlacements.description')}
							/>
						)}
					</>
				)}
			</>
		</>
	);
}
